import { useEffect, useId, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { Icon } from './Icon';
import { useFocusTrap } from '@/shared/hooks/useFocusTrap';
import { usePresence } from '@/shared/hooks/usePresence';

export interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title?: ReactNode;
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** רוחב מקסימלי מותאם — גובר על size (למשל '1000px'). */
  width?: string;
  /** גובה קבוע לדיאלוג (למשל '74vh') — מייצב את הגובה בין לשוניות; עדיין מוגבל ל-90vh. */
  height?: string;
  /** false — הגוף בלי padding והילדים שולטים בפריסה (טאבים/אזורי גלילה משלהם). */
  padded?: boolean;
  children: ReactNode;
  footer?: ReactNode;
  /**
   * שכבת הערימה. ברירת המחדל 1000 נמצאת מתחת לכרטיס הלקוח (1300),
   * לסרגלים הצפים ולטוסטים (2000) — התרעה חוסמת חייבת לעבור אותם
   * במפורש, אחרת היא נפתחת מוסתרת והמשתמש לא רואה שום דבר.
   */
  zIndex?: number;
}

const SIZES: Record<NonNullable<ModalProps['size']>, string> = {
  sm: '420px',
  md: '600px',
  lg: '800px',
  xl: '1100px',
};

export function Modal({ isOpen, onClose, title, size = 'md', width, height, padded = true, children, footer, zIndex = 1000 }: ModalProps) {
  const titleId = useId();
  const trapRef = useFocusTrap(isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [isOpen, onClose]);

  const { mounted, leaving, content } = usePresence(isOpen, children);
  if (!mounted) return null;

  return createPortal(
    <div
      role="presentation"
      onClick={onClose}
      style={{
        position: 'fixed',
        inset: 0,
        background: 'var(--overlay)',
        backdropFilter: 'blur(3px)',
        WebkitBackdropFilter: 'blur(3px)',
        display: 'grid',
        placeItems: 'center',
        zIndex,
        padding: 16,
        animation: leaving ? 'gcFadeOut .22s ease both' : 'gcFade .3s ease both',
        pointerEvents: leaving ? 'none' : undefined,
      }}
    >
      <div
        ref={trapRef as React.RefObject<HTMLDivElement>}
        role="dialog"
        aria-modal="true"
        aria-labelledby={title ? titleId : undefined}
        tabIndex={-1}
        onClick={(e) => e.stopPropagation()}
        style={{
          width: '100%',
          maxWidth: width ?? SIZES[size],
          height,
          maxHeight: '90vh',
          background: 'var(--surface)',
          borderRadius: 'var(--r-4)',
          border: '1px solid var(--glass-line)',
          boxShadow: 'inset 0 1px 0 var(--glass-hi), var(--shadow-3)',
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          animation: leaving
            ? 'gcModalOut .2s cubic-bezier(.4,0,1,1) both'
            : 'gcDialIn .45s cubic-bezier(.34,1.56,.64,1) both',
        }}
      >
        {title && (
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: 'var(--pad-card)',
              borderBottom: '1px solid var(--line)',
            }}
          >
            <h2 id={titleId} style={{ margin: 0, fontSize: 19, fontWeight: 700, letterSpacing: -0.3 }}>{title}</h2>
            <button
              type="button"
              onClick={onClose}
              aria-label="סגור"
              className="btn ghost close-spin"
              style={{ height: 34, width: 34, padding: 0, borderRadius: 99, background: 'var(--surface-3)' }}
            >
              <Icon name="close" size={18} />
            </button>
          </div>
        )}
        <div style={{ padding: padded ? 'var(--pad-card)' : 0, overflow: 'auto', flex: 1, ...(padded ? {} : { display: 'flex', flexDirection: 'column' }) }}>{content}</div>
        {footer && (
          <div
            style={{
              padding: 'var(--pad-card)',
              borderTop: '1px solid var(--line)',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: 8,
            }}
          >
            {footer}
          </div>
        )}
      </div>
    </div>,
    document.body
  );
}

export interface ConfirmDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message?: string;
  confirmLabel?: string;
  cancelLabel?: string;
  tone?: 'danger' | 'warn' | 'info';
}

export function ConfirmDialog({
  isOpen, onClose, onConfirm, title, message,
  confirmLabel = 'אישור', cancelLabel = 'ביטול', tone = 'danger',
}: ConfirmDialogProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={title}
      size="sm"
      footer={
        <>
          <button type="button" className="btn" onClick={onClose}>
            {cancelLabel}
          </button>
          <button
            type="button"
            className={`btn ${tone === 'danger' ? 'danger' : 'primary'}`}
            onClick={() => { onConfirm(); onClose(); }}
          >
            {confirmLabel}
          </button>
        </>
      }
    >
      {message && <p style={{ margin: 0, color: 'var(--text-2)' }}>{message}</p>}
    </Modal>
  );
}
